import { Formik } from "formik";
import { AlertOctagon, FilePen } from "lucide-react";
import React, { useContext, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Form,
  InputGroup,
  Modal,
  Stack,
} from "react-bootstrap";
import toast from "react-hot-toast";
import * as Yup from "yup";

import claimsService from "../../services/claims.service";

import { ClaimsContext } from "./Claims";

const ClaimModal = ({ showClaimModal, setShowClaimModal }) => {
  const [error, setError] = useState();

  const { setClaims, selectedClaim, setSelectedClaim } =
    useContext(ClaimsContext);

  const { claimId, caseId, reimbursementId, amount, notes } = selectedClaim;

  return (
    <Modal
      show={showClaimModal}
      onHide={() => setShowClaimModal(false)}
      backdrop="static"
      keyboard={false}
      size="sm"
    >
      <Modal.Header>
        <div className="d-flex justify-content-start mb-3">
          <FilePen className="me-2" size={48} />
          <div>
            <Card.Title>Edit Claim</Card.Title>
            <Card.Subtitle>
              Edit the claim's Case ID, Reimbursement ID, Amount, or Notes.
            </Card.Subtitle>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="flex-fill alert-blink mb-3">
            <div className="alert-icon">
              <AlertOctagon
                className="position-relative top-50 start-50 translate-middle"
                size={20}
              />
            </div>
            <div className="alert-message font-weight-bold">
              {error.message}
            </div>
          </Alert>
        )}
        <Formik
          initialValues={{
            caseId: caseId || "",
            reimbursementId: reimbursementId || "",
            amount: amount || "",
            notes: notes || "",
          }}
          validationSchema={Yup.object().shape({
            amount: Yup.string().matches(
              /^\d+(\.\d{1,2})?$/,
              'Amount must contain only digits and/or decimal. E.g. "123" or "123.45"'
            ),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            setSubmitting(true);

            const response = await claimsService.updateClaim(claimId, values);

            if (response.success) {
              toast.success("Successfully updated the claim!");

              setClaims((prevState) => {
                return prevState.map((stateClaim) => {
                  if (stateClaim.claimId === response.updatedClaim.claimId) {
                    stateClaim = response.updatedClaim;
                  }
                  return stateClaim;
                });
              });
              setSelectedClaim((prevState) => {
                return { ...prevState, ...response.updatedClaim };
              });
              setSubmitting(false);
              setShowClaimModal(false);
            } else {
              console.log(response);
              toast.error(
                "Something went wrong updating the claim. Please try again."
              );
              setError(response);
              setSubmitting(false);
            }
          }}
        >
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            handleReset,
            touched,
            dirty,
            isSubmitting,
            errors,
            values,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="mb-0">Case ID</Form.Label>
                <Form.Control
                  type="text"
                  name="caseId"
                  placeholder="Enter Case ID..."
                  value={values.caseId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  isInvalid={Boolean(touched.caseId && errors.caseId)}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.caseId}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="mb-0">Reimbursement ID</Form.Label>
                <Form.Control
                  type="text"
                  name="reimbursementId"
                  placeholder="Enter Reimbursement ID..."
                  value={values.reimbursementId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  isInvalid={Boolean(
                    touched.reimbursementId && errors.reimbursementId
                  )}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.reimbursementId}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Label className="m-0">Amount</Form.Label>
              <InputGroup className="mb-3" hasValidation>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="text"
                  name="amount"
                  placeholder="Enter Amount..."
                  value={values.amount}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  isInvalid={Boolean(touched.amount && errors.amount)}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.amount}
                </Form.Control.Feedback>
              </InputGroup>

              <Form.Group className="mb-3">
                <Form.Label className="m-0">Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  style={{ resize: "none" }}
                  name="notes"
                  placeholder="Additional Notes..."
                  value={values.notes}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  isInvalid={Boolean(touched.notes && errors.notes)}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.notes}
                </Form.Control.Feedback>
              </Form.Group>

              <Stack direction="horizontal">
                <Button
                  type="submit"
                  variant="outline-primary"
                  disabled={isSubmitting || !dirty}
                >
                  Save Changes
                </Button>
                <div className="vr ms-3" />
                <Collapse in={dirty} dimension="width">
                  <div>
                    <Button
                      className="ms-3"
                      variant="outline-danger"
                      onClick={handleReset}
                      disabled={isSubmitting || !dirty}
                    >
                      Reset
                    </Button>
                  </div>
                </Collapse>
                <Button
                  className="ms-3"
                  variant="outline-secondary"
                  onClick={() => {
                    handleReset();
                    setShowClaimModal(false);
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default ClaimModal;
